import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { toast } from 'sonner'
import { WEBMAIL_URL } from '@/lib/constants'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { CopyButton } from '@/components/shared/CopyButton'
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Globe,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  Mail,
  Key,
  Copy,
  Shield,
  AlertTriangle,
} from 'lucide-react'
import type { Domain } from '@/types'

interface DomainSetupWizardProps {
  domain: Domain
  open: boolean
  onClose: () => void
  onUpdated?: () => void
}

interface DnsResult {
  status: 'verified' | 'missing' | 'mismatch' | 'error' | 'pending'
  expected?: string
  actual?: string | null
  error?: string
}

interface CreatedMailbox {
  id: string
  email: string
  password: string
}

const RECORD_ORDER = ['mx', 'spf', 'dkim', 'dmarc'] as const

const RECORD_LABELS: Record<string, { name: string; hint: string }> = {
  mx: { name: 'MX Record', hint: 'Tells other mail servers where to deliver email for your domain.' },
  spf: { name: 'SPF Record', hint: 'Authorizes PurpleToad Mail to send email on behalf of your domain.' },
  dkim: { name: 'DKIM Record', hint: 'Cryptographic signature that proves emails are authentic.' },
  dmarc: { name: 'DMARC Record', hint: 'Tells receiving servers how to handle authentication failures.' },
}

export function DomainSetupWizard({ domain, open, onClose, onUpdated }: DomainSetupWizardProps) {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [verifying, setVerifying] = useState(false)
  const [health, setHealth] = useState<Record<string, DnsResult>>((domain.dns_health || {}) as Record<string, DnsResult>)
  const [mailboxLocalPart, setMailboxLocalPart] = useState('')
  const [mailboxDisplayName, setMailboxDisplayName] = useState('')
  const [mailboxPassword, setMailboxPassword] = useState('')
  const [generatePassword, setGeneratePassword] = useState(true)
  const [creatingMailbox, setCreatingMailbox] = useState(false)
  const [createdMailbox, setCreatedMailbox] = useState<CreatedMailbox | null>(null)
  // CHECKPOINT: PRD-09 FR-9.1.4 — Domain ownership TXT verification state.
  const [txtRecord, setTxtRecord] = useState<{ name: string; value: string } | null>(null)
  const [txtVerified, setTxtVerified] = useState(false)
  const [txtVerifying, setTxtVerifying] = useState(false)
  const [txtSkipped, setTxtSkipped] = useState(false)

  useEffect(() => {
    if (open) {
      const allVerified = RECORD_ORDER.every((k) => health[k]?.status === 'verified')
      setStep(allVerified ? 2 : 1)
      setCreatedMailbox(null)
      setTxtSkipped(false)
      fetchTxtVerification()
    }
  }, [open])

  useEffect(() => {
    setHealth((domain.dns_health || {}) as Record<string, DnsResult>)
  }, [domain.dns_health])

  const allVerified = useMemo(
    () => RECORD_ORDER.every((k) => health[k]?.status === 'verified'),
    [health]
  )

  const fetchTxtVerification = async () => {
    try {
      const res = await api.get(`/domains/${domain.id}/verification`)
      const data = res.data
      setTxtRecord({
        name: data.txt_record_name || data.record_name || `_purpletoad.${domain.name}`,
        value: data.txt_record_value || data.record_value || data.value || '',
      })
      setTxtVerified(data.verified === true || data.status === 'verified')
    } catch {
      setTxtRecord({ name: `_purpletoad.${domain.name}`, value: '' })
      setTxtVerified(false)
    }
  }

  const verifyTxt = async () => {
    setTxtVerifying(true)
    try {
      const res = await api.post(`/domains/${domain.id}/verify`)
      const data = res.data
      if (data.verified === true || data.status === 'verified') {
        setTxtVerified(true)
        toast.success('Domain ownership TXT verified')
        onUpdated?.()
      } else {
        toast.error('TXT record not found or incorrect. Please check the record and try again.')
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'TXT verification failed')
    } finally {
      setTxtVerifying(false)
    }
  }

  const verifyDns = async (silent = false) => {
    setVerifying(true)
    try {
      const res = await api.post(`/domains/${domain.id}/verify-dns`)
      const data = res.data
      setHealth((data.results || data.dns_health || domain.dns_health || {}) as Record<string, DnsResult>)
      if (data.all_verified || data.dev_bypass) {
        if (!silent) toast.success(data.dev_bypass ? 'Activated via dev bypass' : 'DNS verified!')
        setStep(3)
        onUpdated?.()
      } else if (!silent) {
        toast.error('Some DNS records are still missing or incorrect.')
      }
    } catch (err: any) {
      if (!silent) toast.error(err.response?.data?.detail?.message || 'Verification failed')
    } finally {
      setVerifying(false)
    }
  }

  // Auto-check DNS every 20 seconds while wizard is open on step 2
  useEffect(() => {
    if (!open || step !== 2) return
    const interval = setInterval(() => verifyDns(true), 20000)
    return () => clearInterval(interval)
  }, [open, step, domain.id])

  const createMailbox = async () => {
    if (!mailboxLocalPart.trim()) {
      toast.error('Enter a mailbox name')
      return
    }
    setCreatingMailbox(true)
    try {
      const payload: Record<string, unknown> = {
        domain_id: domain.id,
        local_part: mailboxLocalPart.trim(),
        display_name: mailboxDisplayName.trim() || undefined,
      }
      if (!generatePassword) {
        payload.password = mailboxPassword
      }
      const res = await api.post('/mailboxes', payload)
      setCreatedMailbox({
        id: res.data.id,
        email: res.data.email,
        password: res.data.password,
      })
      onUpdated?.()
    } catch (err: any) {
      const detail = err.response?.data?.detail
      let message = 'Failed to create mailbox'
      if (Array.isArray(detail) && detail.length > 0) {
        message = detail[0].msg || detail[0].message || message
      } else if (typeof detail === 'string') {
        message = detail
      } else if (detail?.message) {
        message = detail.message
      }
      toast.error(message)
    } finally {
      setCreatingMailbox(false)
    }
  }

  const recordCopyText = (record: { type: string; host: string; value: string; priority?: number }) => {
    const prio = record.priority !== undefined ? `${record.priority} ` : ''
    return `${record.type}\t${record.host}\t${prio}${record.value}`
  }

  const StatusIcon = ({ status }: { status: string }) => {
    if (status === 'verified') return <CheckCircle2 className="h-4 w-4 text-emerald-400" />
    if (status === 'pending') return <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
    return <XCircle className="h-4 w-4 text-red-400" />
  }

  const steps = ['Ownership', 'DNS records', 'Check DNS', 'Mailbox']

  const StepIndicator = () => (
    <ol className="grid grid-cols-4 gap-2 border-y border-border/70 py-4" aria-label="Domain setup progress">
      {steps.map((label, index) => {
        const number = index + 1
        const isCurrent = number === step
        const isComplete = number < step
        return (
          <li key={label} className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
                isComplete ? 'bg-emerald-500 text-white' : isCurrent ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                {isComplete ? <CheckCircle2 className="h-3 w-3" /> : number}
              </span>
              <span className={`hidden truncate text-[10px] font-medium sm:block ${isCurrent ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
            </div>
            <div className={`mt-2 h-1 rounded-full ${number <= step ? 'bg-primary' : 'bg-muted'}`} />
          </li>
        )
      })}
    </ol>
  )

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
              <Globe className="h-4 w-4 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-base tracking-[-0.02em]">Set up {domain.name}</DialogTitle>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {step === 1 && 'Verify domain ownership with a TXT record'}
                {step === 2 && 'Add the DNS records below to your domain provider'}
                {step === 3 && 'Verify your DNS configuration'}
                {step === 4 && 'Create your first mailbox'}
              </p>
            </div>
          </div>
        </DialogHeader>

        <StepIndicator />

        {step === 1 && (
          // CHECKPOINT: PRD-09 FR-9.1.4 — Ownership TXT verification step in domain setup wizard.
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm leading-6 text-amber-200">
              <Shield className="h-4 w-4 shrink-0 mt-0.5" />
              Add this TXT record to prove you own {domain.name}. You can skip this step, but outbound sending will be blocked until ownership is verified.
            </div>

            <Card className="overflow-hidden border-border/70">
              <CardContent className="p-0">
                <div className="flex items-start justify-between gap-3 border-b border-border/70 bg-muted/30 px-4 py-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase w-8">TXT</span>
                      <span className="text-sm font-mono">{txtRecord?.name || `_purpletoad.${domain.name}`}</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Ownership verification record</p>
                  </div>
                  <CopyButton text={txtRecord?.name || `_purpletoad.${domain.name}`} />
                </div>
                <div className="px-4 py-3">
                  <div className="flex items-start gap-2 text-sm font-mono text-muted-foreground">
                    <span className="text-xs shrink-0">→</span>
                    <span className="break-all">{txtRecord?.value || 'Loading...'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {txtVerified && (
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-200 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Ownership TXT verified.
              </div>
            )}

            {txtSkipped && !txtVerified && (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-sm text-amber-200 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                Ownership verification skipped. Outbound sending will be blocked until you verify this record.
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-2">
              <Button variant="outline" onClick={onClose}>
                I'll do this later
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setTxtSkipped(true)} disabled={txtVerified}>
                  Skip
                </Button>
                <Button onClick={verifyTxt} disabled={txtVerifying || txtVerified}>
                  {txtVerifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Shield className="mr-2 h-4 w-4" />
                  Verify TXT
                </Button>
                <Button onClick={() => setStep(2)} disabled={!txtVerified && !txtSkipped}>
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm leading-6 text-amber-200">
              DNS changes can take a few minutes to several hours to propagate. Most providers apply them within 15 minutes.
            </div>

            <div className="space-y-3">
              {RECORD_ORDER.map((key) => {
                const record = domain.dns_records?.[key]
                const info = RECORD_LABELS[key]
                if (!record) return null
                return (
                  <Card key={key} className="overflow-hidden border-border/70">
                    <CardContent className="p-0">
                      <div className="flex items-start justify-between gap-3 border-b border-border/70 bg-muted/30 px-4 py-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold uppercase w-8">{record.type}</span>
                            <span className="text-sm font-mono">{record.host}</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{info.hint}</p>
                        </div>
                        <CopyButton text={recordCopyText(record)} />
                      </div>
                      <div className="px-4 py-3">
                        <div className="flex items-start gap-2 text-sm font-mono text-muted-foreground">
                          <span className="text-xs shrink-0">→</span>
                          <span className="break-all">{record.value}</span>
                        </div>
                        {record.priority !== undefined && (
                          <p className="text-[11px] text-muted-foreground mt-1">Priority: {record.priority} · TTL: {record.ttl}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>

            <div className="flex items-center justify-between gap-3 pt-2">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button onClick={() => verifyDns()} disabled={verifying}>
                {verifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Verify DNS
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="space-y-2">
              {RECORD_ORDER.map((key) => {
                const result = health[key] || { status: 'pending' }
                const record = domain.dns_records?.[key]
                return (
                  <div
                    key={key}
                    className={`flex items-start justify-between gap-3 rounded-xl border px-4 py-3 ${
                      result.status === 'verified'
                        ? 'border-emerald-500/20 bg-emerald-500/5'
                        : 'border-red-500/20 bg-red-500/5'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <StatusIcon status={result.status} />
                        <span className="text-sm font-medium capitalize">{key}</span>
                        <Badge variant="secondary" className="text-[10px] h-5">
                          {result.status}
                        </Badge>
                      </div>
                      {result.error && (
                        <p className="text-xs text-red-400 mt-1">{result.error}</p>
                      )}
                      {result.actual && result.status !== 'verified' && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Found: <span className="font-mono break-all">{result.actual}</span>
                        </p>
                      )}
                      {record && result.status === 'verified' && (
                        <p className="text-xs text-emerald-400 mt-1">
                          Matches expected {record.type} record
                        </p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {!allVerified && (
              <div className="text-sm text-muted-foreground">
                Keep the wizard open and we'll recheck every 20 seconds. You can also click Verify DNS to check now.
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-2">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to records
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => verifyDns()} disabled={verifying}>
                  <RefreshCw className={`mr-2 h-4 w-4 ${verifying ? 'animate-spin' : ''}`} />
                  {verifying ? 'Checking…' : 'Verify DNS'}
                </Button>
                <Button onClick={() => setStep(4)} disabled={!allVerified}>
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            {!createdMailbox ? (
              <>
                <div className="flex items-center gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-200">
                  <CheckCircle2 className="h-4 w-4" />
                  {domain.name} is verified and ready for mail.
                </div>

                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="local-part">Mailbox address</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="local-part"
                        value={mailboxLocalPart}
                        onChange={(e) => setMailboxLocalPart(e.target.value)}
                        placeholder="you"
                        className="flex-1"
                      />
                      <span className="text-sm text-muted-foreground shrink-0">@{domain.name}</span>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="display-name">Display name (optional)</Label>
                    <Input
                      id="display-name"
                      value={mailboxDisplayName}
                      onChange={(e) => setMailboxDisplayName(e.target.value)}
                      placeholder="Your Name"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      id="generate-password"
                      type="checkbox"
                      checked={generatePassword}
                      onChange={(e) => setGeneratePassword(e.target.checked)}
                      className="rounded border-input"
                    />
                    <Label htmlFor="generate-password" className="text-sm font-normal">
                      Auto-generate a secure password
                    </Label>
                  </div>

                  {!generatePassword && (
                    <div className="space-y-1.5">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        type="password"
                        value={mailboxPassword}
                        onChange={(e) => setMailboxPassword(e.target.value)}
                        placeholder="••••••••"
                      />
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between gap-3 pt-2">
                  <Button variant="outline" onClick={() => setStep(3)}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button onClick={createMailbox} disabled={creatingMailbox}>
                    {creatingMailbox && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <Mail className="mr-2 h-4 w-4" />
                    Create Mailbox
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-4 text-center">
                  <Mail className="h-6 w-6 text-emerald-400 mx-auto mb-1" />
                  <p className="text-sm font-medium">Mailbox created</p>
                  <p className="text-xs text-muted-foreground">{createdMailbox.email}</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Key className="h-3.5 w-3.5" />
                    Password (copy now — shown once)
                  </Label>
                  <div className="flex items-center gap-2 min-w-0">
                    <code className="flex-1 min-w-0 bg-muted rounded px-2 py-1.5 text-xs break-all">
                      {createdMailbox.password}
                    </code>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        navigator.clipboard.writeText(createdMailbox.password)
                        toast.success('Password copied')
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex flex-col gap-2 pt-2">
                  <Button
                    onClick={() => {
                      onClose()
                      navigate(`/mailboxes/${createdMailbox.id}`)
                    }}
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Configure email client
                  </Button>
                  <Button variant="outline" asChild>
                    <a href={WEBMAIL_URL} target="_blank" rel="noopener noreferrer">
                      <Globe className="mr-2 h-4 w-4" />
                      Open webmail
                    </a>
                  </Button>
                  <Button variant="ghost" onClick={onClose}>
                    Done
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
